import React, { useEffect, useRef, useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { getJson } from '../../../utils/http'
import { useTranslation } from 'react-i18next'
import { stayDayOptions, calcMaxNumberOfChildren } from '../../search-bar/utils'
import { DatePicker } from '../../date-picker'
import { DataType } from '../../date-picker/types'
import { IconPlus, IconMinus } from './Icon'
import { dayOptions, monthOptions, yearOptions } from '../../jpe-top-page/utils'
import { DataSearchType, KeyDataSearch, DATA_SEARCH_INIT, ChildBirthday } from './type'
import { buildCheckInDateUrl } from './utils'
import { usePlanStore } from '../../../store/planStore'
import { useLpAvailabilityPageStore } from '../../../store/lpAvailabilityPageStore'

type Props = {
  initDataSearch: DataSearchType
  onChangeDataSearch: (data: DataSearchType, checkInDates: DataType[]) => void
  isShowCategoryNameOptions?: boolean
  inputType: 'peopleCount' | 'stayDay'
}

const isMobileScreen = window.innerWidth < 768
const isTabletScreen = window.innerWidth < 1024 && window.innerWidth > 780
const isSmallMobileScreen = window.innerWidth <= 360

// プラン詳細のカレンダー（client/components/plan-calendar）でも使う
export const YearPagination = ({ currentYear, availableYears, setCurrentYear }) => {
  const { isJpe } = useLpAvailabilityPageStore()

  return (
    <div className="flex items-center justify-center">
      <button
        className={`px-3 py-1 text-gray-600 hover:text-black ${
          availableYears.indexOf(currentYear) <= 0 ? 'cursor-not-allowed invisible' : 'hover:cursor-pointer'
        }`}
        onClick={() => {
          const currentIndex = availableYears.indexOf(currentYear)
          if (currentIndex > 0) {
            setCurrentYear(availableYears[currentIndex - 1])
          }
        }}
        disabled={availableYears.indexOf(currentYear) <= 0}
        aria-label="Previous year"
      >
        <span>&#10094;</span>
      </button>
      <div className="mx-4 text-lg">
        {currentYear}
        {isJpe ? '' : '年度'}
      </div>
      <button
        className={`px-3 py-1 text-gray-600 hover:text-black ${
          availableYears.indexOf(currentYear) >= availableYears.length - 1
            ? 'cursor-not-allowed invisible'
            : 'hover:cursor-pointer'
        }`}
        onClick={() => {
          const currentIndex = availableYears.indexOf(currentYear)
          if (currentIndex < availableYears.length - 1) {
            setCurrentYear(availableYears[currentIndex + 1])
          }
        }}
        disabled={availableYears.indexOf(currentYear) >= availableYears.length - 1}
        aria-label="Next year"
      >
        <span>&#10095;</span>
      </button>
    </div>
  )
}

export const ModalFilterPlanByCalendar: React.FC<Props> = ({ initDataSearch, onChangeDataSearch, inputType }) => {
  const [dataSearch, setDataSearch] = useState<DataSearchType>({ ...initDataSearch })
  const [modalSearchHeight, setModalSearchHeight] = useState(0)
  const [checkInDates, setCheckInDates] = useState<DataType[]>([])
  const [modalSearchContentHeight, setModalSearchContentHeight] = useState(0)
  const [clickedReset, setClickedReset] = useState(false)
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())
  const { isJpe } = useLpAvailabilityPageStore()
  const { t } = useTranslation()
  const modalSearchRef = useRef(null)
  const modalSearchContentRef = useRef(null)
  const STAY_DAY_OPTIONS = useMemo(() => (isJpe ? stayDayOptions(t).filter((i) => i.value !== '6') : stayDayOptions(t)), [])
  // 年・月・日リストを用意
  const DAY_OPTIONS = useMemo(() => dayOptions(), [])
  const MONTH_OPTIONS = useMemo(() => monthOptions(t), [t])
  const YEAR_OPTIONS = useMemo(() => yearOptions(), [])
  const { validationErrors, setValidationErrors, openModalFilter, setOpenModalFilter } = usePlanStore()

  const isContentOverHeight = modalSearchContentHeight > modalSearchHeight
  const checkInDateUrl = buildCheckInDateUrl(window.location.pathname)

  // Extract available years from checkInDates
  const availableYears = useMemo(() => {
    if (!checkInDates || checkInDates.length === 0) return [new Date().getFullYear()]

    const years = checkInDates.map((item) => {
      const date = new Date(item.date)
      return date.getFullYear()
    })
    return [...new Set(years)].sort()
  }, [checkInDates])

  useEffect(() => {
    setCurrentYear(availableYears.length > 0 ? availableYears[0] : new Date().getFullYear())
  }, [availableYears])

  useEffect(() => {
    setModalSearchHeight(modalSearchRef?.current?.clientHeight)
    setModalSearchContentHeight(modalSearchContentRef?.current?.clientHeight)
    // Scroll to stay-day-title if inputType is stayDay
    if (inputType === 'stayDay') {
      document.getElementById('stay-day-title').scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  // 生年月日が変更された時にpreschooler/studentフィールドをクリア
  useEffect(() => {
    // 既存のpreschooler/studentフィールドもクリア（統一したので使わない）
    setDataSearch((prev) => ({
      ...prev,
      number_of_preschoolers: 0,
      number_of_students: 0,
    }))
  }, [dataSearch.birthdays])

  useEffect(() => {
    const params = {
      ...dataSearch,
      start_date: '',
      birthdays: dataSearch.birthdays
        .filter((birthday) => birthday.year && birthday.month && birthday.day)
        .map((birthday) => `${birthday.year}-${birthday.month}-${birthday.day}`)
        .join(','),
    }
    getJson(checkInDateUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      params,
    })
      .then((data) => {
        if (!data) return

        const checkInDates = (data.check_in_dates as DataType[]) ?? []
        if (checkInDates.length === 0 || checkInDates.some((checkInDate) => checkInDate.date === dataSearch.start_date)) {
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, start_date: '' }))
        }

        setCheckInDates(checkInDates)
        return
      })
      .catch(() => {})
  }, [
    dataSearch.number_of_adults,
    dataSearch.number_of_preschoolers_1,
    dataSearch.number_of_students_1,
    dataSearch.number_of_study_abroad,
    dataSearch.stay_day,
    JSON.stringify(dataSearch.birthdays),
  ])

  const handleChangeDataSearch = (
    key: KeyDataSearch,
    value: number | string | ChildBirthday,
    type?: 'down' | 'up' | 'option' | 'text',
  ) => {
    let newValue: number | string | ChildBirthday = value

    if (key === 'start_date' && value instanceof Date && value.getFullYear() !== currentYear) {
      setCurrentYear(value.getMonth() < 3 ? value.getFullYear() - 1 : value.getFullYear())
    }

    switch (type) {
      case 'up':
      case 'down': {
        let increment = type === 'up' ? 1 : -1
        if (type == 'down') {
          if (key === 'number_of_adults' && Number(dataSearch[key]) <= 1) {
            increment = 0
          }

          if (
            ['number_of_preschoolers', 'number_of_students'].includes(key) &&
            Number(dataSearch.number_of_preschoolers) + Number(dataSearch.number_of_students) <= 1
          ) {
            increment = 0
          }
        }

        let potentialNewValue = Number(dataSearch[key]) + increment
        // 大人・留学ありのカウンターは最小値1、その他は0
        const minValue = key === 'number_of_adults' || key === 'number_of_study_abroad' ? 1 : 0
        newValue = Math.max(minValue, Math.min(20, potentialNewValue))
        const updateDataSearch = { ...dataSearch }

        // 「留学あり」統合形式用生年月日配列の管理
        if (key === 'number_of_study_abroad') {
          const arr = [...(dataSearch.birthdays || [])]
          arr.length = newValue as number
          for (let i = 0; i < arr.length; i++) {
            arr[i] = arr[i] || { year: '', month: '', day: '' }
          }
          updateDataSearch.birthdays = arr
        }

        setDataSearch({ ...updateDataSearch })
        break
      }
      case 'option': {
        if (key === 'stay_day') {
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, stay_day: value as string }))
          return
        }
        break
      }
      case 'text': {
        const isValueChosen = dataSearch[key] === value
        newValue = isValueChosen ? '' : value
        break
      }
    }

    setDataSearch((prevDataSearch) => ({ ...prevDataSearch, [key]: newValue }))
  }

  const handleClearForm = () => {
    const resetDataSearch = {
      ...DATA_SEARCH_INIT,
    }
    setClickedReset(true)
    setDataSearch({ ...resetDataSearch })
    setValidationErrors({
      childBirthdate: false,
      minChildren: false,
    })
  }

  const handleValidateForm = (value: DataSearchType) => {
    const validationResult = {
      isValid: true,
      errors: {
        childBirthdate: false,
        minChildren: false,
      },
    }

    // こども人数が1名以上であることのバリデーション
    const totalChildren = calcMaxNumberOfChildren(
      value.number_of_study_abroad,
      value.number_of_preschoolers_1,
      value.number_of_students_1,
    )
    if (totalChildren < 1) {
      validationResult.isValid = false
      validationResult.errors.minChildren = true
    }

    // 「留学あり」のバリデーション
    if (value.number_of_study_abroad > 0) {
      if (!value.birthdays || value.birthdays.length < value.number_of_study_abroad) {
        validationResult.isValid = false
        validationResult.errors.childBirthdate = true
      } else {
        const isAllChildBirthdaysFilled = value.birthdays.every((info) => info.year && info.month && info.day)
        if (!isAllChildBirthdaysFilled) {
          validationResult.isValid = false
          validationResult.errors.childBirthdate = true
        }
      }
    }

    return validationResult
  }

  const handleSubmit = () => {
    const validationResult = handleValidateForm(dataSearch)
    if (!validationResult.isValid) {
      setValidationErrors(validationResult.errors)
      modalSearchContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setValidationErrors({
      childBirthdate: false,
      minChildren: false,
    })

    if (clickedReset) {
      setClickedReset(false)
      window.location.href = window.location.pathname
      return
    }
    onChangeDataSearch(dataSearch, checkInDates)
  }

  const ChildTypeCounter = ({
    label,
    dataKey = 'maximum_number_of_children',
    minValue = 0,
  }: {
    label: string
    dataKey?: string
    minValue?: number
  }) => {
    return (
      <div className="w-full grid grid-cols-12 mt-3 mb-3 items-center">
        <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
          <div>{label}</div>
        </div>
        <div
          className={`${
            isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
          } flex items-center gap-2 h-8 ml-auto`}
        >
          <button
            onClick={() => handleChangeDataSearch(dataKey as KeyDataSearch, '', 'down')}
            className="div rounded-full cursor-pointer select-none disabled:opacity-30"
            disabled={dataSearch?.[dataKey] <= minValue}
          >
            <IconMinus />
          </button>
          <div className="mt-1 w-6 text-center">{dataSearch?.[dataKey]}</div>
          <button onClick={() => handleChangeDataSearch(dataKey as KeyDataSearch, '', 'up')} className="all-unset cursor-pointer">
            <IconPlus />
          </button>
        </div>
      </div>
    )
  }

  const handleCloseModalFilter = () => {
    handleClearForm()
    setOpenModalFilter(false)
  }

  if (!openModalFilter) return
  const renderChildrenTypeControl = () => {
    const handleOnChangeInBirthdayField = (value: ChildBirthday, index: number) => {
      const updateDataSearch = { ...dataSearch }
      if (!updateDataSearch.birthdays) updateDataSearch.birthdays = []
      updateDataSearch.birthdays[index] = value
      setDataSearch({ ...updateDataSearch })
    }

    return (
      <>
        {/* 新しい統合形式「留学あり」 */}
        <ChildTypeCounter
          label={t('plan_detail.reservation.modal.people.study_abroad')}
          dataKey="number_of_study_abroad"
          minValue={1}
        />
        {Array.from({ length: dataSearch.number_of_study_abroad }).map((_, idx) => {
          const birthday = dataSearch.birthdays[idx] || { year: '', month: '', day: '' }
          return (
            <div key={idx} className="mb-2">
              <div className="text-sm mb-1">
                {t('plan_detail.reservation.modal.people.study_abroad_birthdate', { count: idx + 1 })}
                <span className="text-red-500">*</span>
              </div>
              <div className="flex">
                {isJpe ? (
                  <>
                    <select
                      className="date-selection-day text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.day}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, day: e.target.value }, idx)}
                    >
                      <option value="">Day</option>
                      {DAY_OPTIONS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <span className="col-start-11 col-span-2 mt-auto px-2"></span>

                    <select
                      className="date-selection-month text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.month}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, month: e.target.value }, idx)}
                    >
                      <option value="">Month</option>
                      {MONTH_OPTIONS.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <span className="col-start-11 col-span-2 mt-auto px-2"></span>

                    <select
                      className="date-selection-year text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.year}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, year: e.target.value }, idx)}
                    >
                      <option value="">Year</option>
                      {YEAR_OPTIONS.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <select
                      className="date-selection-year text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection md:w-[143px]"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.year}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, year: e.target.value }, idx)}
                    >
                      <option value="">生年</option>
                      {YEAR_OPTIONS.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                    <span className="col-start-11 col-span-2 mt-auto px-2">年</span>

                    <select
                      className="date-selection-month text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection w-[70px]"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.month}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, month: e.target.value }, idx)}
                    >
                      <option value="">月</option>
                      {MONTH_OPTIONS.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.value}
                        </option>
                      ))}
                    </select>
                    <span className="col-start-11 col-span-2 pl-2 ml-1 mt-auto px-2 md:ml-0">月</span>

                    <select
                      className="date-selection-day text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection w-[70px]"
                      style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                      value={birthday.day}
                      onChange={(e) => handleOnChangeInBirthdayField({ ...birthday, day: e.target.value }, idx)}
                    >
                      <option value="">日</option>
                      {DAY_OPTIONS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <span className="col-start-11 col-span-2 pl-2 ml-4 mt-auto md:pl-2 md:ml-0">日</span>
                  </>
                )}
              </div>
            </div>
          )
        })}

        {/* 留学なし（未就学児・小学生） */}
        <ChildTypeCounter label={t('plan_detail.reservation.modal.people.not_and_toddler')} dataKey="number_of_preschoolers_1" />
        <ChildTypeCounter label={t('plan_detail.reservation.modal.people.not_and_elementary')} dataKey="number_of_students_1" />
      </>
    )
  }

  return ReactDOM.createPortal(
    <div
      className="modal-search-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto md:inset-0 max-h-full h-full justify-center items-center flex"
      role="dialog"
    >
      <div
        className="relative w-full overflow-y-auto"
        style={{
          width: isMobileScreen ? '100%' : '420px',
          display: isMobileScreen ? 'block' : 'flex',
          alignItems: 'normal',
          height: isMobileScreen ? '100%' : isTabletScreen ? '77%' : '85%',
          animation: 'slide-down 0.4s ease-in-out forwards',
          borderRadius: isMobileScreen ? '0px' : '22px',
        }}
      >
        <div
          className="shadow-sm dark:bg-gray-700"
          ref={modalSearchRef}
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: isMobileScreen ? 'white' : 'transparent',
            borderRadius: isMobileScreen ? '0px' : '22px',
            marginLeft: isMobileScreen ? '0px' : 'auto',
            marginRight: isMobileScreen ? '0px' : 'auto',
          }}
        >
          <div
            className="flex bg-white items-center justify-between px-2 rounded-t"
            style={{
              borderRadius: isMobileScreen ? '0' : '16px 16px 0px 0px',
              height: isSmallMobileScreen ? '11%' : '7%',
            }}
          >
            <button
              className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
              type="button"
              onClick={handleCloseModalFilter}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div
            ref={modalSearchContentRef}
            className="md:mb-0 bg-white"
            style={{
              height: '80%',
              overflow: 'auto',
              padding: isSmallMobileScreen ? '10px' : '0rem 1rem 0.5rem',
            }}
          >
            {isJpe && <h2 className="font-size-20 font-bold mb-3">Enter your child’s details to see matching preschools</h2>}
            <h3 className="mb-3 font-semibold dark:text-white text-black font-size-18">
              {t('plan_detail.reservation.modal.people.title')}
            </h3>
            {validationErrors.minChildren && (
              <div
                id="min-children-notice"
                style={{
                  color: '#DC2626',
                  border: `1px solid #FCA5A5`,
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '10px',
                  margin: '15px 0px',
                  backgroundColor: '#FEF2F2',
                }}
              >
                {t('plan_detail.reservation.modal.people.min_children_notice')}
              </div>
            )}
            {validationErrors.childBirthdate && (
              <div
                id="birthdate-notice"
                style={{
                  color: '#007CFF',
                  border: `1px solid #E5E7EB`,
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '10px',
                  margin: '15px 0px',
                  backgroundColor: '#EFF6FE',
                }}
              >
                {t('plan_detail.reservation.modal.people.birthdate_notice')}
              </div>
            )}
            <div className="w-full grid grid-cols-12 mb-2">
              <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                <div className={`title ${!isJpe ? 'font-bold' : ''}`}>{t('plan_detail.reservation.modal.people.adult')}</div>
              </div>
              <div
                className={`${
                  isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
                } flex items-cennter gap-2 h-8 ml-auto`}
              >
                <button
                  onClick={() => handleChangeDataSearch('number_of_adults', '', 'down')}
                  className="all-unset div rounded-full cursor-pointer select-none rounded-bl-full"
                >
                  <IconMinus />
                </button>
                <div className="text-center mt-1 w-6">{dataSearch.number_of_adults}</div>
                <button
                  className="all-unset rounded-full cursor-pointer rounded-bl-full md:ml-0"
                  onClick={() => handleChangeDataSearch('number_of_adults', '', 'up')}
                >
                  <IconPlus />
                </button>
              </div>
            </div>
            <div className={`title font-bold ${isJpe ? 'hidden' : ''}`}>{t('plan_detail.reservation.modal.people.child')}</div>
            {renderChildrenTypeControl()}
            <h3
              id="stay-day-title"
              className={`mt-4 font-semibold dark:text-white text-black font-size-18 ${isJpe ? 'hidden' : ''}`}
            >
              {t('top.modal_search.stay_day.title')}
            </h3>
            <div className="mt-2 flex items-center justify-start gap-2">
              <span>{t('top.modal_search.stay_period')}</span>
              <select
                className="rounded-lg text-sm children-option border-gray-300 text-ellipsis"
                style={{
                  width: isSmallMobileScreen ? '105px' : '120px',
                }}
                value={dataSearch.stay_day}
                onChange={(e) => handleChangeDataSearch('stay_day', e.target.value, 'option')}
              >
                {STAY_DAY_OPTIONS.map((stayDay) => (
                  <option key={stayDay.id} value={stayDay.value} className="text-sm">
                    {stayDay.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6" style={{ marginBottom: '-30px' }}>
              <YearPagination currentYear={currentYear} availableYears={availableYears} setCurrentYear={setCurrentYear} />
            </div>
            <div className="mt-2">
              <DatePicker
                onChangeDataSearch={handleChangeDataSearch}
                initDate={dataSearch.start_date ? new Date(dataSearch.start_date) : undefined}
                full
                stayDay={Number(dataSearch.stay_day)}
                data={checkInDates}
                startMonth={currentYear ? new Date(currentYear, 3, 1) : undefined}
                endMonth={currentYear ? new Date(currentYear + 1, 2, 1) : undefined}
              />
            </div>
            {availableYears.length > 1 && (
              <YearPagination currentYear={currentYear} availableYears={availableYears} setCurrentYear={setCurrentYear} />
            )}
          </div>
          <div
            className="flex w-full items-center p-4 space-x-2 rounded-b bg-white"
            style={{
              borderRadius: isMobileScreen ? '0px' : '0px 0px 16px 16px',
              position: isMobileScreen || isContentOverHeight ? 'fixed' : 'unset',
              height: isMobileScreen || isTabletScreen ? '12%' : '10%',
            }}
          >
            <button
              className="w-1/2 md:w-1/2 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-hidden focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 py-2.5"
              style={{
                padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
              }}
              onClick={handleClearForm}
              type="button"
            >
              {t('top.filtering.button.clear')}
            </button>

            <button
              className="w-1/2 md:w-1/2 background-green-primary text-white focus:ring-4 focus:outline-hidden focus:ring-blue-300 font-medium rounded-lg text-sm px-5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 py-2.5"
              style={{
                padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
              }}
              type="button"
              onClick={handleSubmit}
            >
              {t('top.filtering.button.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
